import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { listContests, toggleContestActive, deleteContest } from '../../lib/contests';
import { Timestamp } from 'firebase/firestore';

// Helper to convert Timestamp | number to milliseconds
function toMillis(value: Timestamp | number | undefined): number {
  if (!value) return Date.now();
  if (typeof value === 'number') return value;
  if (value instanceof Timestamp) return value.toMillis();
  // Fallback: check if it has toMillis method
  if (typeof (value as any).toMillis === 'function') return (value as any).toMillis();
  return Date.now();
}

type ContestReward = {
  id: string;
  type: 'contest' | 'discount';
  title: string;
  description: string;
  pixRequired: number;
  startAt: Timestamp | number;
  endAt: Timestamp | number;
  isActive: boolean;
  partnerName?: string;
  discountCode?: string;
  maxEntries?: number | null;
};

function getStatus(item: ContestReward): 'Active' | 'Inactive' | 'Upcoming' | 'Ended' {
  if (!item.isActive) return 'Inactive';
  
  const now = Date.now();
  const start = toMillis(item.startAt);
  const end = toMillis(item.endAt);
  
  if (now < start) return 'Upcoming';
  if (now > end) return 'Ended';
  return 'Active';
}

function formatDate(timestamp: Timestamp | number | undefined): string {
  if (!timestamp) return '—';
  const ms = toMillis(timestamp);
  if (ms === 0) return '—';
  return new Date(ms).toLocaleString();
}

export default function ContestsList() {
  const [items, setItems] = useState<ContestReward[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const navigate = useNavigate();

  useEffect(() => {
    console.log('[ContestsList] Component mounted, loading contests...');
    setLoading(true);
    setError(null);
    listContests()
      .then((data) => {
        console.log('[ContestsList] Loaded contests:', data);
        setItems(data as ContestReward[]);
      })
      .catch((err) => {
        console.error('[ContestsList] Error loading contests:', err);
        setError(err.message || 'Failed to load contests');
      })
      .finally(() => {
        console.log('[ContestsList] Loading complete');
        setLoading(false);
      });
  }, []);

  const handleToggleActive = async (id: string, currentActive: boolean) => {
    try {
      await toggleContestActive(id, !currentActive);
      // Update local state
      setItems(items.map(item => 
        item.id === id ? { ...item, isActive: !currentActive } : item
      ));
    } catch (err) {
      console.error('Error toggling active status:', err);
      alert('Failed to update active status');
    }
  };

  const handleDelete = async (id: string, title: string) => {
    if (!window.confirm(`Are you sure you want to delete "${title}"?`)) return;
    try {
      await deleteContest(id);
      setItems(items.filter(item => item.id !== id));
    } catch (err) {
      console.error('Error deleting contest:', err);
      alert('Failed to delete contest');
    }
  };

  return (
    <div className="p-6">
      <div className="flex items-center justify-between mb-4">
        <h1 className="text-2xl font-bold">Contests & Rewards</h1>
        <Link to="/contests/new" className="px-3 py-2 bg-black text-white rounded hover:bg-gray-800">
          Create New
        </Link>
      </div>
      
      {loading && <div className="text-gray-500">Loading contests...</div>}
      
      {error && (
        <div className="p-4 bg-red-50 border border-red-200 rounded text-red-700">
          <div className="font-semibold">Error loading contests</div>
          <div className="text-sm mt-1">{error}</div>
        </div>
      )}
      
      {!loading && !error && items.length === 0 && (
        <div className="text-gray-500 p-4">No contests found. Create your first contest or discount!</div>
      )}
      
      {!loading && !error && items.length > 0 && (
        <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Title</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Type</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">PIX Required</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Start Date</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">End Date</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Entry Count</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {items.map((item) => {
                  const status = getStatus(item);
                  const statusColor = 
                    status === 'Active' ? 'text-green-600 bg-green-50' :
                    status === 'Inactive' ? 'text-gray-600 bg-gray-50' :
                    status === 'Upcoming' ? 'text-blue-600 bg-blue-50' :
                    'text-red-600 bg-red-50';
                  
                  return (
                    <tr key={item.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-medium text-gray-900">{item.title}</div>
                        {item.partnerName && (
                          <div className="text-sm text-gray-500">{item.partnerName}</div>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`px-2 py-1 text-xs font-semibold rounded-full ${
                          item.type === 'contest' ? 'bg-purple-100 text-purple-800' : 'bg-orange-100 text-orange-800'
                        }`}>
                          {item.type === 'contest' ? 'Contest' : 'Discount'}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {item.pixRequired} PIX
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`px-2 py-1 text-xs font-semibold rounded-full ${statusColor}`}>
                          {status}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {formatDate(item.startAt)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {formatDate(item.endAt)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        — {/* Placeholder for future entry count */}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                        <div className="flex items-center gap-2">
                          <Link
                            to={`/contests/${item.id}`}
                            className="text-blue-600 hover:text-blue-900"
                          >
                            Edit
                          </Link>
                          <button
                            onClick={() => navigate(`/contests/${item.id}/entries`)}
                            className="text-green-600 hover:text-green-900"
                            title="View Entries (stub)"
                          >
                            View Entries
                          </button>
                          <button
                            onClick={() => handleToggleActive(item.id, item.isActive)}
                            className={`${item.isActive ? 'text-yellow-600 hover:text-yellow-900' : 'text-green-600 hover:text-green-900'}`}
                            title={item.isActive ? 'Deactivate' : 'Activate'}
                          >
                            {item.isActive ? 'Deactivate' : 'Activate'}
                          </button>
                          <button
                            onClick={() => handleDelete(item.id, item.title)}
                            className="text-red-600 hover:text-red-900"
                            title="Delete"
                          >
                            Delete
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}

